import React, { FC, useState, useCallback, useMemo, useEffect } from "react";
import {
  DayModifiers,
  DayPickerMultipleProps,
  DayProps,
  Matcher,
} from "react-day-picker";
import { SwitchTransition } from "react-transition-group";

import { addMonths, isAfter } from "date-fns";
import moment from "moment";

import { customClassNames, modifiersClassNames } from "./ClassNames";
import { CustomNavBarElement } from "./CustomNavBarElement";
import {
  StyledDatePicker,
  StyledDatePickerCSSTransition,
  StyledDayPicker,
} from "./Styles";
import { YearMonthPicker } from "../Pickers/YearMonthPicker";

const formatWeekdayName = (str: Date) => moment(str).format("ddd").charAt(0);

export interface IDatePicker extends Omit<DayPickerMultipleProps, "mode"> {
  /** Calls when value is picked */
  onChange?: (dates?: { from: Date; to: Date }) => void;
  /** Called when day is clicked */
  onDayClick?: (day: Date, modifiers: DayModifiers) => void;
  /** Set selected day or days */
  selectedDays?: { from?: Date | undefined; to?: Date | undefined };
  /** Set selected day or days */
  disabledDays?: Matcher | Matcher[] | undefined;
  /** Set selection range */
  selectionMode?: string;
  /** Set is date required */
  dateRequired?: boolean;
  /** Set first day of week */
  firstDayOfWeek?: 0 | 1 | 2 | 3 | 4 | 5 | 6;
  /** Set fixed number of weeks in month */
  fixedWeeks?: boolean;
  /** Render day callback */
  renderDay?: (props: DayProps) => JSX.Element | null;
}

export const DatePicker: FC<IDatePicker> = ({
  className,
  onChange,
  onDayClick,
  selectedDays,
  disabledDays,
  selectionMode = "daily",
  month = new Date(),
  onMonthChange = (): null => null,
  dateRequired = false,
  firstDayOfWeek = 0,
  fixedWeeks = true,
  modifiers: defaultModifiers,
  renderDay,
  ...rest
}) => {
  const [enteredTo, setEnteredTo] = useState<Date>();
  const [enteredFrom, setEnteredFrom] = useState<Date>();
  const [stopScroll, setStopScroll] = useState<Date>();
  const [direction, setDirection] = useState(true);
  const [isPreselected, setIsPreselected] = useState(() => {
    return (
      selectedDays?.from instanceof Date && selectedDays?.to instanceof Date
    );
  });
  const [showMonthPicker, setShowMonthPicker] = useState(
    selectionMode === "quarterly" || selectionMode === "monthly"
  );
  const [modifiers, setModifiers] = useState(defaultModifiers);
  const week = firstDayOfWeek === 0 ? "week" : "isoWeek";

  useEffect(() => {
    if (enteredFrom instanceof Date && enteredTo instanceof Date) {
      setModifiers((prev) => {
        return { ...prev, hovered: { from: enteredFrom, to: enteredTo } };
      });
    } else {
      setModifiers(defaultModifiers);
    }
  }, [enteredTo, enteredFrom, defaultModifiers]);

  const handleDayClick = useCallback(
    (day: Date, modifiers: DayModifiers) => {
      if (onDayClick) {
        onDayClick(day, modifiers);
      }
      if (!onChange) {
        return;
      }
      if (month.getMonth() !== day.getMonth()) {
        onMonthChange(day);
      }
      if (modifiers["day_disabled"] || selectionMode === "none") {
        return;
      }
      // reset the selectedDays if the user clicks on the same day
      if (
        !dateRequired &&
        ((selectedDays?.from &&
          moment(day).isSame(moment(selectedDays.from), "day")) ||
          (selectedDays?.to &&
            moment(day).isSame(moment(selectedDays.to), "day")))
      ) {
        setEnteredTo(undefined);
        setEnteredFrom(undefined);

        return onChange(undefined);
      }

      if (selectionMode === "daily") {
        return onChange({ from: day, to: day });
      }

      if (selectionMode === "weekly") {
        if (
          !dateRequired &&
          selectedDays?.from &&
          moment(day).isSame(moment(selectedDays.from), week)
        ) {
          return onChange(undefined);
        }
        return onChange({
          from: moment(day).startOf(week).toDate(),
          to: moment(day).endOf(week).toDate(),
        });
      }

      if (selectionMode === "custom") {
        if (isPreselected) {
          setIsPreselected(false);
          return onChange({ from: day, to: day });
        }
        if (
          !selectedDays ||
          (selectedDays?.from &&
            selectedDays?.to &&
            !moment(selectedDays.from).isSame(moment(selectedDays.to)))
        ) {
          if (
            !dateRequired &&
            selectedDays?.from &&
            selectedDays?.to &&
            moment(day).isBetween(
              moment(selectedDays.from),
              moment(selectedDays.to)
            )
          ) {
            setEnteredTo(day);
            setEnteredFrom(day);
            return onChange(undefined);
          }
          setEnteredFrom(day);
          return onChange({ from: day, to: day });
        }
        if (selectedDays?.from && isAfter(day, selectedDays.from)) {
          setEnteredTo(undefined);
          setEnteredFrom(undefined);
          return onChange({ from: selectedDays.from, to: day });
        }
        if (selectedDays?.to) {
          setEnteredTo(undefined);
          setEnteredFrom(undefined);
          return onChange({ from: day, to: selectedDays.to });
        }
        return onChange({ from: day, to: day });
      }
    },
    [
      onDayClick,
      onChange,
      month,
      selectionMode,
      dateRequired,
      selectedDays,
      onMonthChange,
      week,
      isPreselected,
    ]
  );

  const isSelectedFirstDay = useCallback(() => {
    if (selectionMode !== "custom") {
      return false;
    }
    if (selectedDays?.from && selectedDays?.to) {
      return moment(selectedDays.from).isSame(selectedDays.to);
    }
  }, [selectionMode, selectedDays]);

  const handleDayMouseEnter = useCallback(
    (day: Date) => {
      if (isPreselected && selectionMode !== "weekly") {
        setEnteredTo(day);
        setEnteredFrom(day);
        return;
      }
      if (selectionMode === "daily") {
        setEnteredTo(day);
        setEnteredFrom(day);
      }
      if (selectionMode === "weekly") {
        setEnteredFrom(moment(day).startOf(week).toDate());
        setEnteredTo(moment(day).endOf(week).toDate());
      }

      if (
        selectionMode !== "weekly" &&
        (!selectedDays ||
          (selectedDays?.from &&
            selectedDays?.to &&
            !moment(selectedDays.from).isSame(moment(selectedDays.to))))
      ) {
        setEnteredTo(day);
        setEnteredFrom(day);
      }

      if (isSelectedFirstDay() && selectedDays?.from) {
        setEnteredFrom(selectedDays.from);
        setEnteredTo(day);
      }
    },
    [selectionMode, isSelectedFirstDay, selectedDays, week, isPreselected]
  );

  const onDayMouseLeave = useCallback(() => {
    if (
      selectionMode === "weekly" ||
      selectionMode === "daily" ||
      selectionMode === "custom"
    ) {
      setEnteredFrom(undefined);
      setEnteredTo(undefined);
    }
  }, [setEnteredFrom, setEnteredTo, selectionMode]);

  const onWheel = useCallback(
    (e: { deltaY: number }) => {
      if (
        stopScroll &&
        (new Date().getTime() - stopScroll.getTime()) / 1000 < 0.9
      ) {
        return;
      }
      const nextMonth = showMonthPicker
        ? addMonths(month, 12)
        : addMonths(month, 1);
      const previousMonth = showMonthPicker
        ? addMonths(month, -12)
        : addMonths(month, -1);
      if (e.deltaY > 0.9) {
        setDirection(true);
        onMonthChange(nextMonth);
        setStopScroll(new Date());
      }
      if (e.deltaY < -0.9) {
        setDirection(false);
        onMonthChange(previousMonth);
        setStopScroll(new Date());
      }
    },
    [stopScroll, showMonthPicker, month, onMonthChange]
  );

  const togglePicker = useCallback(() => {
    if (selectionMode !== "monthly" && selectionMode !== "quarterly") {
      return setShowMonthPicker(!showMonthPicker);
    }
    setShowMonthPicker(true);
  }, [showMonthPicker, selectionMode]);

  const handleYearMonthChange = useCallback(
    (m: Date) => {
      if (onChange) {
        if (selectionMode === "monthly") {
          if (
            !dateRequired &&
            selectedDays?.from &&
            moment(m).isSame(moment(selectedDays.from), "month")
          ) {
            return onChange(undefined);
          }
          onChange({
            from: moment(m).startOf("month").toDate(),
            to: moment(m).endOf("month").toDate(),
          });
        }
        if (selectionMode === "quarterly") {
          if (
            !dateRequired &&
            selectedDays?.from &&
            moment(m).isSame(
              moment(selectedDays.from).startOf("quarter"),
              "quarter"
            )
          ) {
            return onChange(undefined);
          }
          onChange({
            from: moment(m).startOf("quarter").toDate(),
            to: moment(m).endOf("quarter").toDate(),
          });
        }
      }
      onMonthChange(m);
    },
    [onMonthChange, onChange, selectedDays, selectionMode, dateRequired]
  );

  const text = useMemo(() => {
    if (showMonthPicker) {
      return `${month.getFullYear()}`;
    }
    return `${moment(month).format("MMMM")} ${
      moment(month).isSame(moment(), "year") ? "" : month.getFullYear()
    }`;
  }, [showMonthPicker, month]);

  const customNavBarElement = useCallback(
    (props) => {
      return (
        <div className="c-DayPicker-wrapper">
          <CustomNavBarElement
            {...props}
            showMonthPicker={showMonthPicker}
            month={month}
            onChangeDirection={setDirection}
            onMonthChange={onMonthChange}
            onClick={togglePicker}
          />
          <YearMonthPicker
            {...props}
            text={text}
            onClick={togglePicker}
            month={month}
            selectedDays={selectedDays}
            onChange={handleYearMonthChange}
            isShown={showMonthPicker}
            selectionMode={selectionMode}
          />
        </div>
      );
    },
    [
      showMonthPicker,
      month,
      onMonthChange,
      togglePicker,
      text,
      selectedDays,
      handleYearMonthChange,
      selectionMode,
    ]
  );

  const onKeyDownCallback = useCallback(
    (e: KeyboardEvent) => {
      // Right Arrow
      if (e.key === "ArrowRight") {
        setDirection(true);
        if (showMonthPicker) {
          onMonthChange(new Date(month.getFullYear() + 1, month.getMonth()));
        } else {
          onMonthChange(new Date(month.getFullYear(), month.getMonth() + 1));
        }
      }
      // Left Arrow
      if (e.key === "ArrowLeft") {
        setDirection(false);
        if (showMonthPicker) {
          onMonthChange(new Date(month.getFullYear() - 1, month.getMonth()));
        } else {
          onMonthChange(new Date(month.getFullYear(), month.getMonth() - 1));
        }
      }
      // Up Arrow
      if (e.key === "ArrowUp") {
        setDirection(true);
        onMonthChange(new Date(month.getFullYear() + 1, month.getMonth()));
      }
      // Down Arrow
      if (e.key === "ArrowDown") {
        setDirection(false);
        onMonthChange(new Date(month.getFullYear() - 1, month.getMonth()));
      }
    },
    [showMonthPicker, onMonthChange, month]
  );

  useEffect(() => {
    document.addEventListener("keydown", onKeyDownCallback);
    return (): void => {
      document.removeEventListener("keydown", onKeyDownCallback);
    };
  }, [month, onMonthChange, showMonthPicker, onKeyDownCallback]);

  const selected = useMemo((): Date[] => {
    const dates: Date[] = [];
    if (!selectedDays?.from) return [];

    // If from and to dates are the same, return an array with one item
    if (moment(selectedDays?.from).isSame(selectedDays?.to, "day")) {
      dates.push(selectedDays?.from);
      return dates;
    }

    let currentDate = moment(selectedDays?.from);

    while (currentDate.isSameOrBefore(selectedDays?.to, "day")) {
      dates.push(currentDate.toDate());
      currentDate = currentDate.add(1, "days");
    }

    return dates;
  }, [selectedDays?.from, selectedDays?.to]);

  return (
    <StyledDatePicker className="c-date-picker-wrapper" onWheel={onWheel}>
      <SwitchTransition mode="out-in">
        <StyledDatePickerCSSTransition
          key={showMonthPicker ? month.getFullYear() : month.getMonth()}
          timeout={300}
          classNames="c-date-picker__animation"
          $direction={direction}
        >
          <StyledDayPicker
            {...rest}
            mode="multiple"
            showOutsideDays
            disabled={disabledDays}
            selected={selected}
            onDayClick={handleDayClick}
            modifiers={modifiers}
            onDayMouseEnter={handleDayMouseEnter}
            onDayMouseLeave={onDayMouseLeave}
            className={className}
            $isYearlyView={showMonthPicker}
            month={month}
            components={{
              Caption: customNavBarElement,
              DayContent: renderDay,
            }}
            classNames={customClassNames}
            modifiersClassNames={modifiersClassNames}
            fixedWeeks={fixedWeeks}
            weekStartsOn={firstDayOfWeek}
            formatters={{
              formatWeekdayName: formatWeekdayName,
            }}
          />
        </StyledDatePickerCSSTransition>
      </SwitchTransition>
    </StyledDatePicker>
  );
};

DatePicker.displayName = "DatePicker";
